import { Injectable, inject } from '@angular/core';
import { Resolution } from '@api-types/enums/resolution.enum';
import { TranslateService } from '@ngx-translate/core';
import Highcharts from 'highcharts';
import { Observable, map, tap } from 'rxjs';

// implement Angular Service
@Injectable({ providedIn: 'root' })
export class HighchartsUtilService {
  private readonly translateService = inject(TranslateService);

  constructor() {
    this.translateService.onLangChange.subscribe(() => {
      this.setHighchartLanguage().subscribe();
    });
  }

  getDateFormat(resolution: Resolution | undefined): string {
    switch (resolution) {
      case Resolution.Daily:
        return '%a, %e %b %Y';
      case Resolution.Weekly:
        return '%e %b %Y';
      case Resolution.Monthly:
        return '%b %Y';
      case Resolution.Yearly:
        return '%Y';
      default:
        return '%a, %e %b %Y';
    }
  }

  getAdditonalTranslations(): { low: string; high: string } {
    return {
      low: this.translateService.instant('HIGHCHARTS.INTERVAL.LOW'),
      high: this.translateService.instant('HIGHCHARTS.INTERVAL.HIGH'),
    };
  }

  getDateFormatOptions(resolution: Resolution): Partial<Highcharts.Options> {
    const dateFormat = this.getDateFormat(resolution);
    return {
      exporting: {
        csv: {
          dateFormat,
        },
      },
      tooltip: {
        dateTimeLabelFormats: {
          day: dateFormat,
          week: dateFormat,
          month: dateFormat,
          year: dateFormat,
        },
      },
    };
  }

  setHighchartLanguage(): Observable<Partial<Highcharts.LangOptions>> {
    return this.translateService.get('HIGHCHARTS').pipe(
      map((lang: { [key: string]: string | Array<string> }) => {
        const options: Partial<Highcharts.LangOptions> = {
          // fill all lang attributes with default values
          numericSymbols: ['k', 'M', 'B', 'T'],
          contextButtonTitle:
            (lang.CONTEXT_BUTTON_TITLE as string) || 'Chart context menu',
          decimalPoint: (lang.DECIMAL_POINT as string) || '.',
          thousandsSep: (lang.THOUSANDS_SEP as string) || ',',
          downloadCSV: (lang.DOWNLOAD_CSV as string) || 'Download CSV',
          downloadJPEG: (lang.DOWNLOAD_JPEG as string) || 'Download JPEG image',
          downloadPDF: (lang.DOWNLOAD_PDF as string) || 'Download PDF document',
          downloadPNG: (lang.DOWNLOAD_PNG as string) || 'Download PNG image',
          downloadSVG:
            (lang.DOWNLOAD_SVG as string) || 'Download SVG vector image',
          downloadXLS: (lang.DOWNLOAD_XLS as string) || 'Download XLS',
          loading: (lang.LOADING as string) || 'Loading...',
          printChart: (lang.PRINT_CHART as string) || 'Print chart',
          hideData: (lang.HIDE_DATA_TABLE as string) || 'Hide data',
          viewData: (lang.VIEW_DATA_TABLE as string) || 'View/close data table',
          viewFullscreen:
            (lang.VIEW_FULL_SCREEN as string) || 'View in full screen',
          exitFullscreen:
            (lang.EXIT_FULL_SCREEN as string) || 'Exit full screen',
          weekdays: (lang.WEEKDAYS as Array<string>) || [
            'Sunday',
            'Monday',
            'Tuesday',
            'Wednesday',
            'Thursday',
            'Friday',
            'Saturday',
          ],
          shortWeekdays: (lang.SHORT_WEEKDAYS as Array<string>) || [
            'Sun',
            'Mon',
            'Tue',
            'Wed',
            'Thu',
            'Fri',
            'Sat',
          ],
          months: (lang.MONTHS as Array<string>) || [
            'January',
            'February',
            'March',
            'April',
            'May',
            'June',
            'July',
            'August',
            'September',
            'October',
            'November',
            'December',
          ],
          shortMonths: (lang.SHORT_MONTHS as Array<string>) || [
            'Jan',
            'Feb',
            'Mar',
            'Apr',
            'May',
            'Jun',
            'Jul',
            'Aug',
            'Sep',
            'Oct',
            'Nov',
            'Dec',
          ],
          resetZoom: (lang.RESET_ZOOM as string) || 'Reset zoom',
          resetZoomTitle:
            (lang.RESET_ZOOM_TITLE as string) || 'Reset zoom level 1:1',
        };

        return options;
      }),
      tap((options: Partial<Highcharts.LangOptions>) => {
        Highcharts.setOptions({
          lang: options,
        });
      }),
    );
  }
}
